import React, { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setCheckOutConfirmIsOpen } from 'redux/checkOutConfirmInfoSlice'
import useWindowDimensions from 'customHook/useWindowDimensions'
import Spinner from 'components/Spinner';
import EighthFloor from '../FloorPlan/EighthFloor'
import NinthFloor from '../FloorPlan/NinthFloor'
import TenthFloor from '../FloorPlan/TenthFloor'
import Legend from '../Legend'
import ModalCheckOutConfirm from "screens/ModalCheckOutConfirm";
import { useLazyQuery } from '@apollo/client';
import { GET_ALL_VOUCHERS_BY_DATE, SUBSCRIBE_TO_VOUCHERS_UPDATED } from 'graphql/service';
import { Container, MapRow } from './style';
import colors from 'constants/colors'
import { debouncedErrorMessage } from 'utils/messageUtil'

const ManageRooms = ({ selectedDate }) => {

  const dispatch = useDispatch()
  const { width } =useWindowDimensions()
  const scale = width/1440 * 1.35
  const checkOutConfirmInfo = useSelector(
    (state) => state.checkOutConfirmInfoReducer
  );

  const [getAllVouchersByDate,{
    loading,
    data: vouchersData,
    error,
    subscribeToMore,
  }] = useLazyQuery(GET_ALL_VOUCHERS_BY_DATE, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    getAllVouchersByDate({
      variables: {
        input: {
          date: selectedDate
        },
      },
    });
  }, [selectedDate, getAllVouchersByDate]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const { eighthFloorRooms, ninthFloorRooms, tenthFloorRooms, totalRemaining, totalRedeemed } = useMemo(() => {
    const result = {
      eighthFloorRooms: {},
      ninthFloorRooms: {},
      tenthFloorRooms: {},
      totalRemaining: 0,
      totalRedeemed: 0,
    };

    if (!vouchersData?.roomsVoucherSummary) return result;

    vouchersData.roomsVoucherSummary.forEach(voucher => {
      switch (voucher.floor) {
        case '8':
          result.eighthFloorRooms[voucher.roomNumber] = voucher;
          break;
        case '9':
          result.ninthFloorRooms[voucher.roomNumber] = voucher;
          break;
        case '10':
          result.tenthFloorRooms[voucher.roomNumber] = voucher;
          break;
        default:
          break;
      }
      result.totalRemaining += voucher.remaining;
      result.totalRedeemed += voucher.redeemed;
    });
    return result;
  }, [vouchersData]);

  useEffect(() => {

    if(!subscribeToMore) return;
    const unsubscribe = subscribeToMore({
      document: SUBSCRIBE_TO_VOUCHERS_UPDATED,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data?.vouchersUpdated) return;
        const updatedVouchers = subscriptionData.data?.vouchersUpdated;
        if (updatedVouchers.length === 0) {
          return prev;
        }
        const mergedRooms = prev.roomsVoucherSummary.map((voucher) => {
          const updated = updatedVouchers.find(
            (element) => element.roomId === voucher.roomId
          );
          return updated ? updated : voucher;
        });

        return {
          ...prev,
          validRooms: [...mergedRooms],
        };
      },
    });

    return () => {
      unsubscribe();
    };
  }, [subscribeToMore]);
    
  if (loading) return <Spinner />;

  return (
    <Container> 
      <MapRow scale={scale}>
        <div className='floor'>
          <div className='floorNumber'>
            <span className='infoType'>FLOOR</span>
            <span className='number'>8</span>
          </div>
          <EighthFloor scale={scale} backgroundColor={colors.black1} rooms={eighthFloorRooms}/>
        </div>
        <div className='floor'>
          <div className='floorNumber'>
            <span className='infoType'>FLOOR</span>
            <span className='number'>9</span>
          </div>
          <NinthFloor scale={scale} backgroundColor={colors.black1} rooms={ninthFloorRooms}/>
        </div>
        <div className='floor'>
          <div className='floorNumber'>
            <span className='infoType'>FLOOR</span>
            <span className='number'>10</span>
          </div>
          <TenthFloor scale={scale} backgroundColor={colors.black1} rooms={tenthFloorRooms}/>
        </div>
      </MapRow>
      <Legend desktop remaining={totalRemaining} redeemed={totalRedeemed}/>
      <ModalCheckOutConfirm
        isOpen={checkOutConfirmInfo.checkOutConfirmIsOpen}
        setIsOpen={(e) => dispatch(setCheckOutConfirmIsOpen(e))}
        roomString={"room " + checkOutConfirmInfo.roomNumber}
        checkOutBookings={checkOutConfirmInfo.checkOutBookings}
      />
    </Container>
  );
};

export default ManageRooms;
